import { apiFetchJson } from '../http/apiFetch';
import type { DownloadAlertRow, DownloadAlertFilters, DownloadAlertSeverity, PagedDownloadAlerts } from '../../models/downloadAlert';

function asString(value: unknown, fallback = ''): string {
  if (value === null || value === undefined) {
    return fallback;
  }
  return String(value);
}

function asOptionalString(value: unknown): string | null {
  if (value === null || value === undefined) {
    return null;
  }
  const str = String(value).trim();
  return str === '' ? null : str;
}

function asNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function asSeverity(value: unknown): DownloadAlertSeverity {
  const str = asString(value).trim().toLowerCase();
  if (str === 'warning' || str === 'critical') {
    return str;
  }
  return 'info';
}

export function normalizeDownloadAlertRow(row: unknown): DownloadAlertRow | null {
  if (!row || typeof row !== 'object') {
    return null;
  }
  const a = row as Record<string, unknown>;
  const id = asString(a.id ?? a.Id).trim();
  if (!id) {
    return null;
  }
  return {
    id,
    alertType: asString(a.alertType ?? a.AlertType).trim(),
    severity: asSeverity(a.severity ?? a.Severity),
    companyId: asOptionalString(a.companyId ?? a.CompanyId),
    merchantId: asOptionalString(a.merchantId ?? a.MerchantId),
    userId: asOptionalString(a.userId ?? a.UserId),
    correlationId: asOptionalString(a.correlationId ?? a.CorrelationId),
    detectedAt: asString(a.detectedAt ?? a.DetectedAt),
    resolvedAt: asOptionalString(a.resolvedAt ?? a.ResolvedAt),
    recordCount: asNumber(a.recordCount ?? a.RecordCount),
    responseSizeBytes: asNumber(a.responseSizeBytes ?? a.ResponseSizeBytes),
    requestCount: asNumber(a.requestCount ?? a.RequestCount),
    ipAddress: asOptionalString(a.ipAddress ?? a.IpAddress),
    windowMinutes: asNumber(a.windowMinutes ?? a.WindowMinutes),
  };
}

function normalizePagedDownloadAlerts(result: unknown): PagedDownloadAlerts {
  if (!result || typeof result !== 'object') {
    return { items: [], totalCount: 0, page: 1, pageSize: 0 };
  }
  const r = result as Record<string, unknown>;
  const rawItems = r.items ?? r.Items;
  const items = Array.isArray(rawItems)
    ? rawItems.map(normalizeDownloadAlertRow).filter((a): a is DownloadAlertRow => Boolean(a))
    : [];
  return {
    items,
    totalCount: asNumber(r.totalCount ?? r.TotalCount),
    page: asNumber(r.page ?? r.Page),
    pageSize: asNumber(r.pageSize ?? r.PageSize),
  };
}

export async function fetchDownloadAlerts(
  getAccessToken: () => string | undefined,
  filters: DownloadAlertFilters,
  page: number,
  pageSize: number
): Promise<PagedDownloadAlerts> {
  const params = new URLSearchParams();
  if (filters.userId?.trim()) {
    params.set('userId', filters.userId.trim());
  }
  if (filters.companyId?.trim()) {
    params.set('companyId', filters.companyId.trim());
  }
  if (filters.severity?.trim()) {
    params.set('severity', filters.severity.trim());
  }
  if (filters.alertType?.trim()) {
    params.set('alertType', filters.alertType.trim());
  }
  if (filters.includeResolved) {
    params.set('includeResolved', 'true');
  }
  if (filters.fromUtc?.trim()) {
    params.set('fromUtc', filters.fromUtc.trim());
  }
  if (filters.toUtc?.trim()) {
    params.set('toUtc', filters.toUtc.trim());
  }
  params.set('page', String(page));
  params.set('pageSize', String(pageSize));

  const result = await apiFetchJson<unknown>(`/api/admin/security/download-alerts?${params.toString()}`, {
    method: 'GET',
    getAccessToken: async () => getAccessToken(),
  });

  return normalizePagedDownloadAlerts(result);
}
